import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { ItemEntity } from './item.entity';
import { PublicUserEntity } from './public-user.entity';

/**
 * PD-14306 — a storefront customer's request to be told when an item is back
 * in stock ("Notify me").
 *
 * One row per (item, storefront user, account). The request is one-shot:
 * `notifiedAt` is set once the customer has been told, and they are not told
 * again until they ask again. MySQL has no partial unique index, so "one
 * pending request" is a plain unique key instead: asking again clears
 * `notifiedAt` on the same row, and a canceled (soft-deleted) row is
 * recovered rather than inserted a second time.
 *
 * The account is stored because stock on the storefront is scoped to the
 * account's warehouses, so the same item can be out of stock for one of a
 * customer's accounts and available for another.
 */
@Index(
	'idx_item_back_in_stock_subscription_pending',
	['notifiedAt', 'fkItemId'],
	{},
)
@Index('idx_item_back_in_stock_subscription_user', ['fkPublicUserId'], {})
@Index('idx_item_back_in_stock_subscription_deleted', ['deletedDate'], {})
@Index(
	'idx_item_back_in_stock_subscription_unique',
	['fkItemId', 'fkPublicUserId', 'fkClientId'],
	{ unique: true },
)
@Entity('item_back_in_stock_subscription')
export class ItemBackInStockSubscriptionEntity extends BaseEntity {
	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	/** The storefront login that asked; the only one that is notified. */
	@Column('int', { name: 'fk_public_user__id', unsigned: true })
	public fkPublicUserId!: number;

	/** The account the customer was shopping as; its warehouses decide "in stock". */
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/** Null = pending. Set once the customer has been told; cleared when they ask again. */
	@Column('datetime', { name: 'notifiedAt', nullable: true })
	public notifiedAt!: Date | null;

	/** When a notice was last tried without being delivered. */
	@Column('datetime', { name: 'lastAttemptAt', nullable: true })
	public lastAttemptAt!: Date | null;

	/**
	 * Undelivered tries since the customer last asked. Spaces retries out and
	 * stops them after a limit, so a notice that can never be delivered is not
	 * retried on every sweep.
	 */
	@Column('smallint', {
		name: 'attemptCount',
		unsigned: true,
		default: () => "'0'",
	})
	public attemptCount!: number;

	@ManyToOne(() => ItemEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_item__id', referencedColumnName: 'id' }])
	public item!: ItemEntity;

	@ManyToOne(() => PublicUserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_public_user__id', referencedColumnName: 'id' }])
	public publicUser!: PublicUserEntity;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public client!: ClientEntity;
}
